import { useEffect, useRef } from "react";

/**
 * Pin a panel to the viewport while its tall rail scrolls.
 * Uses transform (not top/fixed). Scroll position is rounded to whole
 * pixels and only applied when it changes — cuts mobile subpixel jitter.
 *
 * Panel CSS: position:absolute; top:0; left:0; right:0; height:100vh/dvh;
 * Rail CSS: position:relative; height: N vh (>> 100vh);
 */
export function useDomPin(railRef, panelRef, onProgress) {
  const cbRef = useRef(onProgress);
  cbRef.current = onProgress;

  useEffect(() => {
    const rail = railRef.current;
    const panel = panelRef.current;
    if (!rail || !panel) return undefined;

    let raf = 0;
    let lastScrolled = -1;
    let lastProgress = -1;
    let railDocTop = 0;
    let total = 1;
    let viewH = 0;

    const measure = () => {
      viewH = window.innerHeight;
      railDocTop = Math.round(rail.getBoundingClientRect().top + window.scrollY);
      total = Math.max(rail.offsetHeight - viewH, 1);
    };

    const sync = () => {
      raf = 0;
      const scrolled = Math.round(
        Math.min(Math.max(window.scrollY - railDocTop, 0), total),
      );
      if (scrolled !== lastScrolled) {
        lastScrolled = scrolled;
        panel.style.transform = `translate3d(0, ${scrolled}px, 0)`;
      }
      const progress = scrolled / total;
      // Skip tiny progress noise (iOS rubber-band / address bar)
      if (Math.abs(progress - lastProgress) >= 0.0008) {
        lastProgress = progress;
        cbRef.current?.(progress);
      }
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };

    const onResize = () => {
      measure();
      lastScrolled = -1;
      lastProgress = -1;
      sync();
    };

    measure();
    sync();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [railRef, panelRef]);
}
